/**
 * This file extends the jqueryui dialog widget by setting some defaults and gives it a
 * few extra options we usually use on most of our dialogs
 */
import _ from 'underscore';
import $ from 'jquery';
import { t } from 'app/libWrappers/i18next.wrapper';
import 'jquery-ui-lib';

$.widget('ui.dialog', $.ui.dialog, {
    options: {
        // These are some default options which all of our modals should have, for consistency
        // If any of these has to be changed for a certain dialog box, it should be overwritten when creating the dialog box
        closeOnClickOutside: true, // only if `modal` is true
        closeOnEscape: true,
        onEnterPress: null,
        modal: true,
        show: 1,
        hide: 0,
        draggable: false,
        resizable: false,

        defaultDialogClasses: 'moreover_dialog'
    },

    // This maps some buttons types to classes, so we don't have to repeat the same classes every time we configure
    // the buttons. Instead of adding the classes we only need to add `type: 'primary|secondary'`
    // To overwrite this just don't set a type and instead set the 'class' property instead
    buttonTypesClasses: {
        primary: 'button button--contained button--sm',
        secondary: 'button button--outlined button--sm'
    },

    _create: function () {
        this.options.dialogClass += ' ' + this.options.defaultDialogClasses;
        this.options.closeText = this.options.closeText || t('common:close');

        // Add the needed classes to each button based on its type
        this.options.buttons && this._customizeButtonBehavior(this.options.buttons);

        this._super();

        this.options.buttons && this._removeDefaultButtonClasses();
    },

    open: function () {
        this._disableButtonAutoFocusUntilOpened();

        this._super(); // call original function

        if (this.options.closeOnClickOutside) {
            this._setOnClickOutside();
        }

        if (_.isFunction(this.options.onEnterPress)) {
            this._setOnEnterPress();
        }
    },

    /**
     * Overwrite the "setOption" method to apply our custom changes to the buttons if they are changed
     */
    _setOption: function (option, value) {
        if (option === 'buttons') {
            this._customizeButtonBehavior(value);
        }

        this._super(option, value);

        this._removeDefaultButtonClasses();
    },

    _setOnClickOutside: function () {
        var thisDialog = this;
        if (!_.isUndefined(this.overlay)) {
            this.overlay.on('click', function () {
                thisDialog.close();
            });
        }
    },

    _setOnEnterPress: function () {
        var thisDialog = this;
        this.element.keydown(function (ev) {
            if (ev.keyCode === $.ui.keyCode.ENTER) {
                thisDialog.options.onEnterPress();
                ev.stopPropagation();
                ev.preventDefault();
            }
        });
    },

    /**
     * CKEditor (and some other plugins) has interaction problems when created
     * inside a jqueryui dialog. This will fix those problems
     */
    _allowInteraction: function (event) {
        if (this._super(event)) {
            return true;
        }

        // Fix interaction issues with select2 search field
        if ($(event.target).is('.select2-search__field')) {
            return true;
        }

        // Fix interaction issues with general iframes with the dialog
        if (event.target.ownerDocument !== this.document[0]) {
            return true;
        }

        // Fix interaction issues with dialog window
        if ($(event.target).closest('.cke_dialog').length) {
            return true;
        }

        // Fix interaction issues with iframe based drop downs in IE
        if ($(event.target).closest('.cke').length) {
            return true;
        }

        return false;
    },

    /**
     * Adds the appropriate classes to the buttons based on a property called "type".
     * It uses the "buttonTypesClasses" object to set these classes.
     * e.g. { text: 'Ok', 'type': 'primary' } => { text: 'Ok', class: 'button--contained' }
     * If the button already has a class set, it will append the classes to it
     */
    _customizeButtonBehavior: function (buttons) {
        buttons.forEach((button) => {
            let extraButtonClass = this.buttonTypesClasses[button.type] || '';
            let currentButtonClass = button.class || '';
            button.class = currentButtonClass + ' ' + extraButtonClass;
        });
    },

    _disableButtonAutoFocusUntilOpened: function () {
        let $buttons = this.uiDialog.find('.ui-dialog-buttonset button');
        let originalTabIndexes = [];
        $buttons.each((i, button) => {
            originalTabIndexes[i] = button.tabIndex;
            button.tabIndex = -1;
        });

        let originalFocusCallback = this.options.focus;
        this.options.focus = () => {
            _.isFunction(originalFocusCallback) && originalFocusCallback();
            $buttons.each((i, button) => {
                button.tabIndex = originalTabIndexes[i];
            });
            this.options.focus = originalFocusCallback;
        };
    },

    _removeDefaultButtonClasses: function () {
        this.uiButtonSet
            .find('button')
            .removeClass('ui-button ui-widget ui-state-default ui-state-active ui-state-focus ui-corner-all')
            .off('mouseenter mouseleave mouseover mousedown focus');
    }
});
